/**
 * Client Database (DWA Internal → Accounts) — the community record.
 *
 * The system of record for a client's communities, seeded from the PLC ROT and
 * from here on edited in the platform (decision 2026-08-06: the platform is
 * authoritative, the sheet becomes an export).
 *
 * Shows every community including unlaunched ones — unlike the monitoring
 * registry, which only holds live sites. Lifecycle is three separate facts:
 * where it is (lifecycle_status), whether we poll it (monitoring_enabled), and
 * whether it's actually launched (launched_at, set by "Mark live", never
 * inferred from an HTTP 200).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchClientCommunities, createClientCommunity, updateClientCommunity,
  markClientCommunityLive, deleteClientCommunity, BRAND_CLIENTS,
  type Community, type CommunityInput, type CommunityLifecycle,
  type CommunityListResponse, type BackendClientId,
} from '../services/dashboardApi';
import type { ScorecardTheme } from './ScorecardView';

export interface ClientDatabaseProps {
  theme: ScorecardTheme;
}

const LIFECYCLE_LABEL: Record<CommunityLifecycle, string> = {
  prospective: 'Prospective',
  onboarding: 'Onboarding',
  in_development: 'In development',
  live: 'Live',
  offboarding: 'Offboarding',
  inactive: 'Inactive',
  listing_only: 'Listing only',
  non_community: 'Non-community',
};

const LIFECYCLE_COLOR: Record<CommunityLifecycle, string> = {
  prospective: '#94a3b8',
  onboarding: '#f59e0b',
  in_development: '#0ea5e9',
  live: '#15803d',
  offboarding: '#dc2626',
  inactive: '#94a3b8',
  listing_only: '#8b5cf6',
  non_community: '#64748b',
};

/** Link buttons on the community record. Rendered only when a URL is present,
 *  so an empty Drive link simply doesn't show a dead button. */
const LINKS = [
  { key: 'drive_url', label: 'Drive' },
  { key: 'brochure_url', label: 'Brochure' },
  { key: 'schedule_tour_url', label: 'Tour' },
  { key: 'connect_sales_url', label: 'Sales' },
  { key: 'contact_us_url', label: 'Contact' },
  { key: 'google_maps_url', label: 'Maps' },
  { key: 'facebook_url', label: 'Facebook' },
  { key: 'tso_calendar_url', label: 'TSO' },
] as const;

/** Paid-media flags — read-only here; they feed the Media dashboards. */
const MEDIA_FLAGS = [
  { key: 'ads_enabled', label: 'Ads' },
  { key: 'search_enabled', label: 'Search' },
  { key: 'pmax_enabled', label: 'PMax' },
  { key: 'click_cease_enabled', label: 'ClickCease' },
  { key: 'retargeting_enabled', label: 'Retargeting' },
  { key: 'facebook_ads_enabled', label: 'FB Ads' },
] as const;

function emptyCommunity(): CommunityInput {
  return {
    name: '', domain: '', ownership_group: '', community_group: '',
    community_template: '', site_type: 'Community Site',
    lifecycle_status: 'onboarding', target_launch_date: null,
    monitoring_enabled: false,
    server: '', wpengine_install: '', wh_comm_id: '', care_levels: '',
    main_phone: '', street_address: '', state: '', licensing_number: '',
    google_maps_url: '', facebook_url: '', tso_calendar_url: '', brochure_url: '',
    schedule_tour_url: '', connect_sales_url: '', contact_us_url: '', drive_url: '',
    notes: '',
  };
}

function toInput(c: Community): CommunityInput {
  return {
    name: c.name, domain: c.domain ?? '', ownership_group: c.ownership_group ?? '',
    community_group: c.community_group ?? '', community_template: c.community_template ?? '',
    site_type: c.site_type ?? '', lifecycle_status: c.lifecycle_status,
    target_launch_date: c.target_launch_date ?? null,
    monitoring_enabled: c.monitoring_enabled,
    server: c.server ?? '', wpengine_install: c.wpengine_install ?? '',
    wh_comm_id: c.wh_comm_id ?? '', care_levels: c.care_levels ?? '',
    main_phone: c.main_phone ?? '', street_address: c.street_address ?? '',
    state: c.state ?? '', licensing_number: c.licensing_number ?? '',
    google_maps_url: c.google_maps_url ?? '', facebook_url: c.facebook_url ?? '',
    tso_calendar_url: c.tso_calendar_url ?? '', brochure_url: c.brochure_url ?? '',
    schedule_tour_url: c.schedule_tour_url ?? '', connect_sales_url: c.connect_sales_url ?? '',
    contact_us_url: c.contact_us_url ?? '', drive_url: c.drive_url ?? '',
    notes: c.notes ?? '',
  };
}

const Badge: React.FC<{ status: CommunityLifecycle }> = ({ status }) => (
  <span style={{
    fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
    padding: '0.12rem 0.45rem', borderRadius: 3, color: '#fff',
    background: LIFECYCLE_COLOR[status], whiteSpace: 'nowrap',
  }}>{LIFECYCLE_LABEL[status]}</span>
);

// ─── Editor ─────────────────────────────────────────────────────────────────

const Editor: React.FC<{
  theme: ScorecardTheme;
  initial: CommunityInput;
  choices: CommunityListResponse['choices'];
  editingId: number | null;
  saving: boolean;
  onSave: (c: CommunityInput) => void;
  onCancel: () => void;
}> = ({ theme, initial, choices, editingId, saving, onSave, onCancel }) => {
  const [c, setC] = useState<CommunityInput>(initial);
  const set = <K extends keyof CommunityInput>(k: K, v: CommunityInput[K]) =>
    setC((p) => ({ ...p, [k]: v }));

  const label: React.CSSProperties = {
    fontSize: '0.68rem', fontWeight: 600, color: theme.textSecondary,
    display: 'block', marginBottom: '0.22rem',
  };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', fontSize: '0.82rem', padding: '0.36rem 0.5rem',
    borderRadius: 4, border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
    color: theme.textPrimary, fontFamily: 'inherit',
  };
  const field = (l: string, node: React.ReactNode) => (<div><label style={label}>{l}</label>{node}</div>);
  const text = (k: keyof CommunityInput, ph = '') => (
    <input style={input} value={(c[k] as string) ?? ''}
      onChange={(e) => set(k, e.target.value as CommunityInput[typeof k])} placeholder={ph} />
  );
  const withCurrent = (opts: string[], cur?: string | null) =>
    cur && !opts.includes(cur) ? [...opts, cur].sort() : opts;

  const grid = (cols: string): React.CSSProperties => ({
    display: 'grid', gridTemplateColumns: cols, gap: '0.6rem',
  });

  return (
    <div style={{
      background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, boxShadow: theme.cardShadow,
      borderRadius: 4, padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.8rem',
    }}>
      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: theme.textPrimary }}>
        {editingId == null ? 'New community' : 'Edit community'}
      </div>

      <div style={grid('2fr 1.5fr 1fr')}>
        {field('Name', text('name', 'e.g. Brentwood at Elkhart AL'))}
        {field('Domain', text('domain', 'example.com'))}
        {field('Site type', (
          <select style={input} value={c.site_type || ''} onChange={(e) => set('site_type', e.target.value)}>
            <option value="">— none —</option>
            <option value="Community Site">Community Site</option>
            <option value="Landing Site">Landing Site</option>
          </select>
        ))}
      </div>

      <div style={grid('1fr 1fr 1fr 1fr')}>
        {field('Lifecycle', (
          <select style={input} value={c.lifecycle_status}
            onChange={(e) => set('lifecycle_status', e.target.value as CommunityLifecycle)}>
            {choices.lifecycle.map((l) => <option key={l} value={l}>{LIFECYCLE_LABEL[l]}</option>)}
          </select>
        ))}
        {field('Target launch', (
          <input type="date" style={input} value={c.target_launch_date || ''}
            onChange={(e) => set('target_launch_date', e.target.value || null)} />
        ))}
        {field('Template', (
          <select style={input} value={c.community_template || ''}
            onChange={(e) => set('community_template', e.target.value || null)}>
            <option value="">— none —</option>
            {withCurrent(choices.templates, c.community_template).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ))}
        {field('Group', (
          <select style={input} value={c.community_group || ''}
            onChange={(e) => set('community_group', e.target.value || null)}>
            <option value="">— none —</option>
            {withCurrent(choices.groups, c.community_group).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ))}
      </div>

      <label style={{ fontSize: '0.78rem', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        <input type="checkbox" checked={c.monitoring_enabled}
          onChange={(e) => set('monitoring_enabled', e.target.checked)} />
        Monitoring enabled
        <span style={{ color: theme.textMuted, fontSize: '0.72rem' }}>
          — can be on before launch to watch the site come up; pre-launch results are informational
        </span>
      </label>

      <div style={grid('1fr 1fr 1fr 1fr')}>
        {field('Ownership', text('ownership_group'))}
        {field('Server', text('server'))}
        {field('WP Engine install', text('wpengine_install'))}
        {field('WH Comm ID', text('wh_comm_id'))}
      </div>

      <div style={grid('1fr 1fr 1fr 1fr')}>
        {field('Care levels', text('care_levels', 'e.g. AL, MC'))}
        {field('Phone', text('main_phone'))}
        {field('State', text('state'))}
        {field('Licensing #', text('licensing_number'))}
      </div>

      {field('Street address', text('street_address'))}

      <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textMuted, marginTop: '0.2rem' }}>
        Links
      </div>
      <div style={grid('1fr 1fr')}>
        {field('Drive folder', text('drive_url', 'https://drive.google.com/…'))}
        {field('Brochure', text('brochure_url'))}
        {field('Schedule a tour', text('schedule_tour_url'))}
        {field('Connect w/ sales', text('connect_sales_url'))}
        {field('Contact us', text('contact_us_url'))}
        {field('Google Maps', text('google_maps_url'))}
        {field('Facebook', text('facebook_url'))}
        {field('TSO calendar', text('tso_calendar_url'))}
      </div>

      {field('Notes', text('notes'))}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{
          fontSize: '0.8rem', padding: '0.45rem 0.9rem', borderRadius: 4,
          border: `1px solid ${theme.cardBorder}`, background: 'transparent',
          color: theme.textSecondary, cursor: 'pointer', fontFamily: 'inherit',
        }}>Cancel</button>
        <button type="button" disabled={!c.name.trim() || saving} onClick={() => onSave(c)} style={{
          fontSize: '0.8rem', fontWeight: 700, padding: '0.45rem 1.1rem', borderRadius: 4,
          border: 'none',
          background: c.name.trim() && !saving ? theme.accent : theme.cardBorder,
          color: c.name.trim() && !saving ? '#111' : theme.textMuted,
          cursor: c.name.trim() && !saving ? 'pointer' : 'default', fontFamily: 'inherit',
        }}>{saving ? 'Saving…' : 'Save community'}</button>
      </div>
    </div>
  );
};

// ─── View ───────────────────────────────────────────────────────────────────

export const ClientDatabaseView: React.FC<ClientDatabaseProps> = ({ theme }) => {
  const [client, setClient] = useState<BackendClientId>('plc');
  const [data, setData] = useState<CommunityListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<{ id: number | null; input: CommunityInput } | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await fetchClientCommunities(client)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setData(null); }
    finally { setLoading(false); }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  const communities = useMemo(() => data?.communities ?? [], [data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return communities.filter((c) => {
      if (statusFilter !== 'all' && c.lifecycle_status !== statusFilter) return false;
      if (!q) return true;
      return [c.name, c.domain, c.community_group, c.community_template, c.state]
        .some((v) => (v ?? '').toLowerCase().includes(q));
    });
  }, [communities, statusFilter, search]);

  const save = async (input: CommunityInput) => {
    if (!editing) return;
    setSaving(true); setError(null);
    try {
      // Blank strings → null, so "unset" is stored as absence rather than ''.
      const payload = Object.fromEntries(
        Object.entries(input).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v]),
      ) as unknown as CommunityInput;
      payload.name = input.name.trim();
      if (editing.id == null) await createClientCommunity(client, payload);
      else await updateClientCommunity(client, editing.id, payload);
      setEditing(null);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const markLive = async (c: Community) => {
    if (!window.confirm(
      `Mark "${c.name}" live?\n\nThis stamps the launch date and turns monitoring on, ` +
      `so Blog and SmashBalloon will start reporting it as a live site.`,
    )) return;
    setError(null);
    try { await markClientCommunityLive(client, c.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const remove = async (c: Community) => {
    if (!window.confirm(`Delete "${c.name}" from the Client Database?`)) return;
    setError(null);
    try { await deleteClientCommunity(client, c.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const smallBtn = (color: string): React.CSSProperties => ({
    fontSize: '0.7rem', fontWeight: 600, padding: '0.25rem 0.5rem', borderRadius: 4,
    border: `1px solid ${theme.cardBorder}`, background: 'transparent', color,
    cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none',
  });

  const flag = (v?: boolean | null) => (v == null ? '—' : v ? 'Yes' : 'No');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>
            Client Database
          </h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            The community record — every community, not just live sites.
            {data && <> · {communities.length} total</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, domain, group…"
            style={{
              fontSize: '0.8rem', padding: '0.35rem 0.55rem', borderRadius: 4, minWidth: 190,
              border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
              color: theme.textPrimary, fontFamily: 'inherit',
            }}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{
            fontSize: '0.8rem', padding: '0.35rem 0.55rem', borderRadius: 4,
            border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
            color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
          }}>
            <option value="all">All statuses</option>
            {Object.entries(data?.counts ?? {}).map(([s, n]) => (
              <option key={s} value={s}>
                {LIFECYCLE_LABEL[s as CommunityLifecycle] ?? s} ({n})
              </option>
            ))}
          </select>
          <select value={client} onChange={(e) => { setClient(e.target.value as BackendClientId); setEditing(null); setExpanded(null); }} style={{
            fontSize: '0.82rem', padding: '0.35rem 0.55rem', borderRadius: 4,
            border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
            color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
          }}>
            {BRAND_CLIENTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {!editing && (
            <button type="button" onClick={() => setEditing({ id: null, input: emptyCommunity() })} style={{
              fontSize: '0.8rem', fontWeight: 700, padding: '0.4rem 0.9rem', borderRadius: 4,
              border: 'none', background: theme.accent, color: '#111', cursor: 'pointer', fontFamily: 'inherit',
            }}>+ Add community</button>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)',
          borderRadius: 4, padding: '0.7rem 0.9rem', fontSize: '0.82rem', color: '#991b1b',
        }}>{error}</div>
      )}

      {data && Object.keys(data.duplicate_wh_ids).length > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 4, padding: '0.7rem 0.9rem', fontSize: '0.8rem', color: theme.textPrimary,
        }}>
          <strong>Duplicate WH Comm IDs</strong> — WelcomeHome CRM binding is ambiguous for these:
          <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
            {Object.entries(data.duplicate_wh_ids).map(([id, names]) => (
              <li key={id}>{id} — {names.join(', ')}</li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <Editor
          theme={theme}
          initial={editing.input}
          choices={data?.choices ?? { templates: [], groups: [], lifecycle: ['prospective', 'onboarding', 'in_development', 'live', 'offboarding', 'inactive', 'listing_only', 'non_community'] }}
          editingId={editing.id}
          saving={saving}
          onSave={(c) => void save(c)}
          onCancel={() => setEditing(null)}
        />
      )}

      {loading && <p style={{ fontSize: '0.85rem', color: theme.textMuted }}>Loading communities…</p>}

      {!loading && communities.length === 0 && !error && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, boxShadow: theme.cardShadow,
          borderRadius: 4, padding: '2rem', textAlign: 'center', fontSize: '0.85rem', color: theme.textSecondary,
        }}>
          No communities yet for this client. Add one, or seed from the ROT with
          {' '}<code>scripts/import_plc_rot.py</code>.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          boxShadow: theme.cardShadow, borderRadius: 4, overflow: 'hidden',
        }}>
          {filtered.map((c) => {
            const isOpen = expanded === c.id;
            return (
              <div key={c.id} style={{ borderTop: `1px solid ${theme.rowDivider}` }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap',
                  padding: '0.55rem 0.75rem',
                }}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : c.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      fontSize: '0.85rem', fontWeight: 600, color: theme.textPrimary,
                      fontFamily: 'inherit', textAlign: 'left', minWidth: 170, flex: '1 1 200px',
                    }}
                  >
                    {isOpen ? '▾ ' : '▸ '}{c.name}
                    <span style={{ display: 'block', fontSize: '0.7rem', color: theme.textMuted, fontWeight: 400 }}>
                      {c.domain || 'no domain'}{c.community_template ? ` · ${c.community_template}` : ''}
                    </span>
                  </button>
                  <Badge status={c.lifecycle_status} />
                  {c.target_launch_date && c.lifecycle_status !== 'live' && (
                    <span style={{ fontSize: '0.72rem', color: theme.textSecondary }}>
                      target {c.target_launch_date}
                    </span>
                  )}
                  {c.monitoring_enabled && (
                    <span style={{ fontSize: '0.68rem', color: theme.textMuted }}>monitored</span>
                  )}
                  <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
                    {LINKS.filter((l) => c[l.key]).map((l) => (
                      <a key={l.key} href={c[l.key] as string} target="_blank" rel="noopener noreferrer"
                        style={smallBtn(theme.textSecondary)}>{l.label}</a>
                    ))}
                    {c.lifecycle_status !== 'live' && (
                      <button type="button" style={smallBtn('#15803d')} onClick={() => void markLive(c)}>
                        Mark live
                      </button>
                    )}
                    <button type="button" style={smallBtn(theme.textSecondary)}
                      onClick={() => setEditing({ id: c.id, input: toInput(c) })}>Edit</button>
                    <button type="button" style={smallBtn('#dc2626')} onClick={() => void remove(c)}>Delete</button>
                  </div>
                </div>

                {isOpen && (
                  <div style={{
                    padding: '0.2rem 0.75rem 0.9rem 1.6rem', display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.5rem 1.2rem',
                    fontSize: '0.76rem', color: theme.textSecondary,
                  }}>
                    <div><strong>Group:</strong> {c.community_group || '—'}</div>
                    <div><strong>Ownership:</strong> {c.ownership_group || '—'}</div>
                    <div><strong>Site type:</strong> {c.site_type || '—'}</div>
                    <div><strong>Server:</strong> {c.server || '—'}</div>
                    <div><strong>WP Engine:</strong> {c.wpengine_install || '—'}</div>
                    <div><strong>WH Comm ID:</strong> {c.wh_comm_id || '—'}</div>
                    <div><strong>Care levels:</strong> {c.care_levels || '—'}</div>
                    <div><strong>Respite:</strong> {flag(c.respite)}</div>
                    <div><strong>MW / LIHTC / MC-EPC:</strong> {flag(c.mw)} / {flag(c.lihtc)} / {flag(c.mc_epc)}</div>
                    <div><strong>Phone:</strong> {c.main_phone || '—'}</div>
                    <div><strong>State:</strong> {c.state || '—'}</div>
                    <div><strong>Licensing #:</strong> {c.licensing_number || '—'}</div>
                    <div><strong>Must show legally:</strong> {flag(c.legally_must_show)}</div>
                    <div><strong>Playbook:</strong> {flag(c.has_playbook)}</div>
                    <div><strong>Launched:</strong> {c.launched_at ? new Date(c.launched_at).toLocaleDateString() : '—'}</div>
                    <div><strong>ROT status:</strong> {c.status_raw || '—'}</div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <strong>Address:</strong> {c.street_address || '—'}
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <strong>Paid media:</strong>{' '}
                      {MEDIA_FLAGS.map((f) => `${f.label} ${flag(c[f.key])}`).join(' · ')}
                    </div>
                    {c.notes && (
                      <div style={{ gridColumn: '1 / -1' }}><strong>Notes:</strong> {c.notes}</div>
                    )}
                    {!c.drive_url && (
                      <div style={{ gridColumn: '1 / -1', color: theme.textMuted, fontStyle: 'italic' }}>
                        No Drive link yet — paste one via Edit and the Drive button appears.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && communities.length > 0 && filtered.length === 0 && (
        <p style={{ fontSize: '0.82rem', color: theme.textMuted }}>
          No communities match this filter.
        </p>
      )}
    </div>
  );
};

export default ClientDatabaseView;
